import type { EnneagramTypeId } from "@/types/supabase";
import type { Playbook } from "./types";

/**
 * Per-type clinical playbook. Source: `09-nove-personalidades.md` do Instituto
 * Rafa Medeiros (faça/não faça/frame de fechamento + assinatura linguística).
 *
 * Read in <15s during the consultation.
 */
export const PLAYBOOKS: Record<EnneagramTypeId, Playbook> = {
  1: {
    id: 1,
    code: "C1",
    name: "Perfeição",
    archetype: "O Perfeccionista / Crítico",
    shortDescription: "Sério, técnico, metódico — máquina de encontrar erros.",
    belief: "O mundo deve ser metodicamente correto e ordenado.",
    doThis: [
      "Desenhe o protocolo etapa por etapa, com prazos exatos",
      "Use vocabulário técnico preciso, sem 'mais ou menos'",
      "Apresente contrato/orçamento meticuloso, sem erros de digitação",
      "Estrutura visual: cada etapa com data clara",
    ],
    avoid: [
      "Imprecisão (\"mais ou menos 15 dias\" → desconfia)",
      "Ambiente desorganizado, roupa suja, erro de digitação",
      "Estética subjetiva sem critério objetivo",
    ],
    closingFrame: "Ordem + precisão. Mostre o método como engenharia.",
    keyPhrase: "Pode me explicar passo a passo?",
    signature: "Pergunta etapas, prazos, contrato.",
  },
  2: {
    id: 2,
    code: "C2",
    name: "Doação",
    archetype: "O Prestativo / Doador",
    shortDescription: "Caloroso, afetivo, quer amizade antes do procedimento.",
    belief: "Sou amado quando cuido dos outros.",
    doThis: [
      "Deixe-o criar vínculo primeiro, não force a anamnese",
      "Frame: \"Como isso beneficia também sua família?\"",
      "Acolha a conversa pessoal, pergunte de volta",
    ],
    avoid: [
      "Cortar conexão afetiva (\"vamos direto à queixa\")",
      "Vender como autoestima/empoderamento — não conecta",
      "Ser frio ou puramente clínico",
    ],
    closingFrame: "A compra cuida de quem ele ama, não dele mesmo.",
    keyPhrase: "Adoro o seu trabalho! O que sugerir eu sigo.",
    signature: "Elogia, toca, pergunta sobre família.",
  },
  3: {
    id: 3,
    code: "C3",
    name: "Resultado",
    archetype: "O Realizador / Vaidoso",
    shortDescription: "Competitivo, vaidoso, quer performance e prazo.",
    belief: "Sou valioso pelo meu sucesso e imagem.",
    doThis: [
      "Seja direto, rápido, foco em resultado",
      "Responda \"em quanto tempo\" sem rodeio",
      "Frame: \"Como isso me ajuda a performar mais?\"",
      "Mostre cases de pessoas de status comparável",
    ],
    avoid: [
      "Explicação longa, ler lista de hormônios em voz alta",
      "Detalhe técnico sem conexão com resultado",
      "Qualquer perda de tempo",
    ],
    closingFrame: "Performance + prazo. Resultado tangível.",
    keyPhrase: "Em quanto tempo verei o resultado?",
    signature: "Lista conquistas, fala rápido, usa \"eu\".",
  },
  4: {
    id: 4,
    code: "C4",
    name: "Originalidade",
    archetype: "O Individualista / Romântico",
    shortDescription: "Sensível, único, busca lugar próprio no mundo.",
    belief: "Sou especial e diferente; preciso encontrar meu lugar único.",
    doThis: [
      "Protocolo personalizado, criado exclusivamente pra ele",
      "Destaque o que é singular, artístico, não-padrão",
      "Linguagem: \"criei isso especificamente para o seu caso\"",
      "Acolha a expressão emocional, não corte se chorar",
    ],
    avoid: [
      "\"Protocolo mais vendido\" → fatal",
      "Qualquer linguagem de commoditização",
      "Comparar a outros pacientes",
    ],
    closingFrame: "Unicidade. Criação exclusiva.",
    keyPhrase: "Quero algo que combine comigo, não algo padronizado.",
    signature: "Reflexivo, metafórico, estética única.",
  },
  5: {
    id: 5,
    code: "C5",
    name: "Sabedoria",
    archetype: "O Investigador / Observador",
    shortDescription: "Reservado, técnico, valoriza privacidade e dados.",
    belief: "Privacidade e conhecimento me protegem do mundo.",
    doThis: [
      "Traga evidência científica, artigos, dados",
      "ÚNICO tipo que aguenta explicação técnica aprofundada",
      "Faça perguntas inteligentes pra ele falar",
      "Respeite o silêncio — é processamento, não desinteresse",
    ],
    avoid: [
      "Perguntar sobre filhos/família = invasão = fecha",
      "Forçar small talk pra criar conexão",
      "Pressão por resposta imediata",
    ],
    closingFrame: "Evidência técnica. Profundidade científica.",
    keyPhrase: "Você tem algum artigo científico sobre isso?",
    signature: "Curto, técnico, pode trazer artigos impressos.",
  },
  6: {
    id: 6,
    code: "C6",
    name: "Segurança",
    archetype: "O Leal / Cético",
    shortDescription: "Hesitante, cético, pesquisa enquanto você fala.",
    belief: "O mundo é perigoso; se não tiver cuidado, serei enganado.",
    doThis: [
      "Marcadores de autoridade, cases, contratos escritos",
      "Garantias claras, por escrito",
      "Deixe a decisão com ele, sem pressão",
      "Aceite múltiplas perguntas sobre risco com paciência",
    ],
    avoid: [
      "NUNCA escassez/urgência (\"só tenho duas vagas\")",
      "Confronto ou pressão de qualquer tipo",
      "Promessa sem suporte concreto",
    ],
    closingFrame: "Segurança + garantia escrita.",
    keyPhrase: "E se algo der errado? Existe garantia?",
    signature: "Pergunta riscos, pesquisa Google/Reclame Aqui.",
  },
  7: {
    id: 7,
    code: "C7",
    name: "Entusiasmo",
    archetype: "O Entusiasta / Visionário",
    shortDescription: "Animado, salta tópicos, ama novidade.",
    belief: "O mundo é cheio de possibilidades; a vida deve ser estimulante.",
    doThis: [
      "Entrega rápida, visual, dinâmica",
      "Conecte ideias de forma não-linear",
      "Frame: novidade + tendência",
      "Perguntas abertas, deixe ele fazer conexões",
    ],
    avoid: [
      "Ler lista de hormônios/exames em voz alta",
      "Explicação biológica monotônica",
      "Formato padrão de consulta longa",
    ],
    closingFrame: "Novidade + tendência. Dinâmica.",
    keyPhrase: "Quais são as últimas tendências?",
    signature: "Interrompe com histórias, alta energia.",
  },
  8: {
    id: 8,
    code: "C8",
    name: "Poder",
    archetype: "O Protetor / Confrontador",
    shortDescription: "Firme, dominante, testa quem é superior.",
    belief: "O mundo é um ringue; preciso ser forte.",
    doThis: [
      "Mantenha autoridade — \"poder só respeita poder\"",
      "Seja direto, confiante, sem pedir desculpa pelo método",
      "Dê sensação de controle MAS mantenha liderança clínica",
      "Diga exatamente o que pensa, sem meias palavras",
    ],
    avoid: [
      "Recuar, demonstrar fraqueza → perde respeito",
      "Pedir desculpa pela metodologia",
      "Entrar em disputa de controle se você também é C8",
    ],
    closingFrame: "Autoridade + controle. Você manda no jogo.",
    keyPhrase: "Quero saber se isso é superior às outras técnicas.",
    signature: "Testa autoridade, fala alto, direto.",
  },
  9: {
    id: 9,
    code: "C9",
    name: "Paz",
    archetype: "O Mediador / Pacificador",
    shortDescription: "Suave, evita conflito, concorda passivo, não decide só.",
    belief: "Para estar bem, preciso evitar conflito e manter paz.",
    doThis: [
      "Seja a figura de poder que ele precisa pra decidir",
      "Frame: \"A vida pede uma palavra: decisão.\"",
      "Cite consequência de inação — gentilmente",
      "Pergunte de quem ele depende pra decidir e inclua",
    ],
    avoid: [
      "Deixar a decisão só com ele → não decide",
      "Qualquer confronto → fuga",
      "Confundir concordância passiva com fechamento",
    ],
    closingFrame: "Impulso externo. Você decide com ele.",
    keyPhrase: "Não sei se esse é o momento certo.",
    signature: "Concorda com tudo, baixa energia.",
  },
};

export function getPlaybook(id: EnneagramTypeId): Playbook {
  return PLAYBOOKS[id];
}
